import React, { useState, useRef } from "react"
import Button from "@mui/material/Button"
import { Box } from "@mui/material"
import TextField from "@mui/material/TextField"
import Editor from "@monaco-editor/react"
import { useSnackbar } from "notistack"
import SearchRoundedIcon from "@mui/icons-material/SearchRounded"

import PageHeading from "components/common/LayoutComponents/PageHeading"
import PageContent from "components/common/LayoutComponents/PageContent"
import { verifyResponse } from "utils/httpRequest"
import LoadingScreen from "components/common/LoadingScreen"
import { PERMISSIONS } from "constants/permission"
import { getRedisValueByKey } from "services/redis"
import { UsePermissionVerification } from "components/customHooks"
import { StyledRedis } from "./Redis.style"
import * as monaco from "monaco-editor"
import { loader } from "@monaco-editor/react"

loader.config({ monaco })

const Redis = () => {
  const [loading, setLoading] = useState(false)
  const [editorData, setEditorData] = useState(null)
  const [searchKeyValue, setSearchKeyValue] = useState("")
  const [hasSearched, setHasSearched] = useState(false)
  const AccessControl = UsePermissionVerification()
  const { enqueueSnackbar } = useSnackbar()

  const searchKeyRef = useRef("")

  const searchRedisValueByKey = async (key) => {
    try {
      setLoading(true)
      const response = await getRedisValueByKey({ key })
      verifyResponse(response)
      setSearchKeyValue(key)
      setEditorData(response.data)
      setHasSearched(true)
    } catch {
      enqueueSnackbar("Failed to query redis value by key.", {
        variant: "warning",
      })
      setEditorData(null)
      setHasSearched(true)
    } finally {
      setLoading(false)
    }
  }

  const handleSearch = () => {
    const searchKey = searchKeyRef.current.value.trim()
    if (searchKey === "") {
      enqueueSnackbar("Please enter a redis key.", { variant: "warning" })
    } else {
      searchRedisValueByKey(searchKey)
    }
  }

  const handleTextInputEnter = (event) => {
    if (event.key === "Enter") {
      event.preventDefault()
      handleSearch()
    }
  }

  const canViewRedis = AccessControl.VerifyAccess([
    PERMISSIONS.RESOURCE.RESOURCE_LIST,
  ])

  const editorValue = (() => {
    if (typeof editorData === "string") return editorData
    if (typeof editorData === "number" || typeof editorData === "boolean") {
      return String(editorData)
    }
    if (editorData === null || editorData === undefined) return ""
    return JSON.stringify(editorData, null, 2)
  })()

  return (
    <PageContent>
      <StyledRedis>
        <PageHeading>
          <div className="heading-copy">
            <h1 className="page-title">Redis Inspector</h1>
            <p className="page-subtitle">
              Query redis values by key for diagnostics and verification.
            </p>
          </div>
        </PageHeading>
        <div className="page-surface">
          {canViewRedis && (
            <>
              <Box
                className="search-bar"
                component="form"
                noValidate
                autoComplete="off"
                onSubmit={(event) => {
                  event.preventDefault()
                  handleSearch()
                }}
              >
                <TextField
                  inputRef={searchKeyRef}
                  id="redis-key-search"
                  label="Redis Key"
                  variant="outlined"
                  size="small"
                  placeholder="Search redis value by key"
                  onKeyDown={handleTextInputEnter}
                />
                <Button
                  className="search-btn"
                  variant="contained"
                  size="medium"
                  startIcon={<SearchRoundedIcon />}
                  onClick={handleSearch}
                >
                  Find
                </Button>
              </Box>

              {searchKeyValue && (
                <div className="search-query-info">
                  Search variable by key <span className="key-value">{searchKeyValue}</span>
                </div>
              )}

              {editorData !== null && (
                <div className="editor-region">
                  <Editor
                    className="json-editor"
                    theme="vs-dark"
                    height="calc(100vh - 390px)"
                    defaultLanguage="json"
                    language="json"
                    value={editorValue}
                    options={{
                      readOnly: true,
                      lineNumbers: "off",
                      formatOnPaste: true,
                      minimap: { enabled: false },
                      scrollBeyondLastLine: false,
                    }}
                  />
                </div>
              )}

              {hasSearched && editorData === null && (
                <div className="page-empty">
                  No value returned for the provided redis key.
                </div>
              )}
            </>
          )}
          {!canViewRedis && (
            <div className="page-empty">
              You do not have permission to access redis inspection tools.
            </div>
          )}
        </div>
      </StyledRedis>
      <LoadingScreen loading={loading} />
    </PageContent>
  )
}

export default Redis
